import { useMemo, useState } from 'react';
import { Box, Sheet, Stack, Table, Typography } from '@mui/joy';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';


/**
 * Универсальная таблица разреза с сортировкой и строкой итогов.
 *
 * Итог считается на клиенте по видимым строкам. Для сумм это корректно,
 * а для производных метрик — нет: средний CTR это не среднее по CTR строк,
 * а сумма кликов на сумму показов. Поэтому производные пересчитываются
 * из сырых, а не усредняются.
 */
export default function AdBreakdownTable({
    columns, rows, defaultSort, emptyText = 'Данных за период нет', maxHeight,
}) {
    const [sortKey, setSortKey] = useState(defaultSort || columns[0]?.key);
    const [sortDesc, setSortDesc] = useState(true);

    const sorted = useMemo(() => {
        const copy = [...(rows || [])];
        copy.sort((a, b) => {
            const av = a?.[sortKey];
            const bv = b?.[sortKey];
            if (typeof av === 'string' || typeof bv === 'string') {
                return sortDesc
                    ? String(bv || '').localeCompare(String(av || ''), 'ru')
                    : String(av || '').localeCompare(String(bv || ''), 'ru');
            }
            return sortDesc ? (Number(bv) || 0) - (Number(av) || 0)
                            : (Number(av) || 0) - (Number(bv) || 0);
        });
        return copy;
    }, [rows, sortKey, sortDesc]);

    const totals = useMemo(() => {
        if (!rows?.length) return null;
        const impressions = rows.reduce((sum, r) => sum + (Number(r.impressions) || 0), 0);
        const clicks = rows.reduce((sum, r) => sum + (Number(r.clicks) || 0), 0);
        const spend = rows.reduce((sum, r) => sum + (Number(r.spend) || 0), 0);
        return {
            impressions,
            clicks,
            spend,
            // Пересчитываем из сырых, а не усредняем по строкам
            ctr: impressions ? (clicks / impressions) * 100 : 0,
            cpc: clicks ? spend / clicks : 0,
            ecpm: impressions ? (spend / impressions) * 1000 : 0,
        };
    }, [rows]);

    const toggleSort = (key) => {
        if (key === sortKey) setSortDesc((v) => !v);
        else { setSortKey(key); setSortDesc(true); }
    };

    if (!rows?.length) {
        return (
            <Sheet variant="soft" sx={{ p: 4, borderRadius: 'md', textAlign: 'center' }}>
                <Typography level="body-sm" color="neutral">{emptyText}</Typography>
            </Sheet>
        );
    }

    return (
        <Sheet
            variant="outlined"
            sx={{ borderRadius: 'md', overflow: 'auto', maxHeight: maxHeight || 'none' }}
        >
            <Table stickyHeader hoverRow sx={{ '--TableCell-paddingY': '9px' }}>
                <thead>
                    <tr>
                        {columns.map((col) => (
                            <th
                                key={col.key}
                                style={{
                                    width: col.width,
                                    cursor: col.sortable === false ? 'default' : 'pointer',
                                    textAlign: col.align || 'left',
                                }}
                                onClick={() => col.sortable !== false && toggleSort(col.key)}
                            >
                                <Stack
                                    direction="row"
                                    spacing={0.2}
                                    alignItems="center"
                                    justifyContent={col.align === 'right' ? 'flex-end' : 'flex-start'}
                                >
                                    <span>{col.label}</span>
                                    {sortKey === col.key && (
                                        sortDesc
                                            ? <ArrowDropDownRoundedIcon sx={{ fontSize: 18 }} />
                                            : <ArrowDropUpRoundedIcon sx={{ fontSize: 18 }} />
                                    )}
                                </Stack>
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((row, index) => (
                        <tr key={row.id ?? row.campaign_id ?? row.creative_id ?? row.code ?? index}>
                            {columns.map((col) => (
                                <td key={col.key} style={{ textAlign: col.align || 'left' }}>
                                    {col.render ? col.render(row) : (row[col.key] ?? '—')}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
                {totals && (
                    <tfoot>
                        <tr>
                            {columns.map((col, index) => (
                                <td key={col.key} style={{ textAlign: col.align || 'left' }}>
                                    <Typography level="body-sm" sx={{ fontWeight: 700 }}>
                                        {index === 0
                                            ? 'Итого'
                                            : (col.total ? col.total(totals) : '')}
                                    </Typography>
                                </td>
                            ))}
                        </tr>
                    </tfoot>
                )}
            </Table>
        </Sheet>
    );
}